import { Button, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Check, Colors } from '@/assets';
import { Alert, Loading, PrimaryButton, showToatify } from '@/components';
import { AuthActions, AuthThunks, useAuth } from '@/features/auth';
import { useIsMountedRef, useMessageData } from '@/hooks';
import { selectErrorCode } from '@/lib/axios';
import { Screens } from '@/routes';

export const Inviation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const { isAuthenticated, user } = useAuth();
    const mountedRef = useIsMountedRef();

    const [loading, setLoading] = useState<boolean>(false);
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();

    const onAcceptInvitation = useCallback(
        async (forceAcceptOtherEmail?: boolean) => {
            try {
                if (!token) return;
                setLoading(true);
                await dispatch(AuthThunks.acceptInvitation(token, forceAcceptOtherEmail));
                navigate(Screens.Dashboard);
                showToatify('Invitation accepted');
            } catch (error: any) {
                if (error.status === 404) {
                    navigate(Screens.Dashboard);
                    return;
                }
                setMessage({ message: error.message });
            } finally {
                if (!mountedRef.current) {
                    return;
                }
                setLoading(false);
            }
        },
        [dispatch, mountedRef, navigate, setMessage, token]
    );

    useEffect(() => {
        if (!isAuthenticated && token) {
            dispatch(AuthActions.setInvitationToken(token));
            navigate(Screens.Signup);
            return;
        }
    }, [dispatch, isAuthenticated, navigate, token]);

    const logout = () => {
        dispatch(AuthActions.logout());
    };

    return (
        <>
            <Loading loading={loading} />
            <Check />
            <Typography variant="h1" sx={{ mt: 3, fontWeight: 600, color: Colors.tangaroaBlue }}>
                Just a moment ...
            </Typography>
            {user && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 'normal', color: Colors.lynchBlue, textAlign: 'center', mt: 2, mb: 4 }}>
                        You are logged in by account: {user.email}
                    </Typography>
                    <PrimaryButton onClick={() => onAcceptInvitation(true)} title="Accept Invitation With This Email" />
                </>
            )}
            <Button onClick={logout} sx={{ color: Colors.lynchBlue, py: 1.5, boxShadow: 'none' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: Colors.lynchBlue, ml: 0.5 }}>
                    Sign Out
                </Typography>
            </Button>
            <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
        </>
    );
};
